import { type CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import { auth, type AuthError } from '@atlaskit/outbound-auth-flow-client';
import { type ExternalUser } from '@atlaskit/user-picker';

import {
	DEFAULT_STARGATE_SERVICE_URL,
	EXUS_ORGS_PATH,
	EXUS_THIRDPARTY_PATH,
	INTEGRATIONS_FETCH_RETRIES_COUNT,
	INTEGRATIONS_PATH,
	ORG_SETTINGS_ACTION,
	SEARCH_ACTION,
	WINDOW_HEIGHT,
	WINDOW_WIDTH,
} from '../constants';
import { FetchError, fetchJson, httpRetryPolicy, retryFetch } from '../util';

import {
	triggerAnalyticsForIntegrationConnected,
	triggerAnalyticsForIntegrationFailed,
} from './analytics';
import type { IntegrationsResponse, OrgSettings } from './types';

const exusThirdPartyUrl = (path: string) =>
	`${DEFAULT_STARGATE_SERVICE_URL}${EXUS_THIRDPARTY_PATH}/${path}`;

const exusOrgsUrl = (path: string) => `${DEFAULT_STARGATE_SERVICE_URL}${EXUS_ORGS_PATH}/${path}`;

export const fetchExistingIntegrations = async (cloudId: string, shouldRetry: boolean = false) => {
	const maxAttempts = shouldRetry ? INTEGRATIONS_FETCH_RETRIES_COUNT : 0;
	const queryString = new URLSearchParams({ cloudId });
	const fetchUrl = exusThirdPartyUrl(INTEGRATIONS_PATH);
	const result = await retryFetch({
		request: () =>
			fetch(`${fetchUrl}?${queryString}`, {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
				},
			}),
		canRetryCheck: httpRetryPolicy({ maxAttempts }),
	});

	if (result.ok) {
		return result.result as IntegrationsResponse;
	} else if (!result.aborted) {
		throw new FetchError(
			`Unable to fetch integrations, status: ${result.code} text: ${result.message}`,
			result.status,
		);
	}
};

export const connectToThirdParty = (
	serviceKey: string,
	analytics: {
		analyticsChannel?: string;
		attributes?: Record<string, any>;
		createAnalyticsEvent: CreateUIAnalyticsEvent;
		teamId?: string;
	},
	callback?: (success: boolean, error?: AuthError) => void,
) => {
	const url = exusThirdPartyUrl(`connect?serviceKey=${serviceKey}`);
	const windowLeft = window.screen.width / 2 - WINDOW_WIDTH / 2;
	const windowTop = window.screen.height / 2 - WINDOW_HEIGHT / 2;
	const options = `menubar, width=${WINDOW_WIDTH}, height=${WINDOW_HEIGHT}, top=${windowTop}, left=${windowLeft}`;

	auth(url, options)
		.then(() => {
			triggerAnalyticsForIntegrationConnected(
				analytics.createAnalyticsEvent,
				serviceKey,
				analytics.analyticsChannel,
				analytics.teamId,
				analytics.attributes,
			);

			callback?.(true);
		})
		.catch((error: AuthError) => {
			triggerAnalyticsForIntegrationFailed(
				analytics.createAnalyticsEvent,
				serviceKey,
				analytics.analyticsChannel,
				error,
				analytics.attributes,
			);

			callback?.(false, error);
		});
};

export const search = async (query: string, cloudId: string): Promise<ExternalUser[]> => {
	const queryString = new URLSearchParams({
		query,
		cloudId,
	});
	const fetchUrl = exusThirdPartyUrl(`search?${queryString}`);
	const resp = await fetchJson<any[]>(fetchUrl, SEARCH_ACTION);

	return resp.map((d: any) => {
		return {
			id: d.email,
			email: d.email,
			name: d.displayName,
			publicName: d.displayName,
			avatarUrl: d.avatarUrl,
			type: 'user',
			isExternal: true,
			sources: d.sources.map((s: string) => s.toLowerCase()),
		} as ExternalUser;
	});
};

export const fetchOrgSettings = async (orgId: string): Promise<OrgSettings> => {
	const fetchUrl = exusOrgsUrl(`${orgId}/settings`);
	return fetchJson<OrgSettings>(fetchUrl, ORG_SETTINGS_ACTION);
};
